import React__default, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { u as useGetPortal } from './useGetPortal-DcrcpIB0.js';
import { u as useStormError } from '../ErrorIdProvider/useStormError-CitRd0-Z.js';

/**
 * Portals provide a first-class way to render children into a DOM node
 * that exists outside the DOM hierarchy of the parent component.
 */
const Portal = ({
  children,
  containerId = '',
  disabled = false,
  externalPortalRef,
  transitionDuration = 0
}) => {
  /** Get the current portal ref */
  const portalRef = useGetPortal(containerId, externalPortalRef);
  const {
    throwStormError
  } = useStormError();

  /**
   * The `portalMounted` and `useEffect()` prevents react from attempting to use the portal
   * before it has been committed to the DOM. The `portalRef.current` will sometimes be `null` \
   * until after the initial render.
   */
  const [portalMounted, setPortalMounted] = useState(!!(portalRef !== null && portalRef !== void 0 && portalRef.current));
  useEffect(() => {
    var _document;
    setPortalMounted(true);
    let timeout;
    const bodyRect = (_document = document) === null || _document === void 0 || (_document = _document.body) === null || _document === void 0 ? void 0 : _document.getBoundingClientRect();
    // this prevents this check from happening in a headless environment
    if (bodyRect && bodyRect.height > 0 && bodyRect.width > 0) {
      timeout = setTimeout(() => {
        var _portalRef$current$ch;
        if (portalRef && !(portalRef !== null && portalRef !== void 0 && portalRef.current)) {
          throwStormError('portal', 'The portal element is currently null or undefined. Ensure the portal element is valid.');
        }
        if (portalRef && portalRef.current && ((_portalRef$current$ch = portalRef.current.children) === null || _portalRef$current$ch === void 0 ? void 0 : _portalRef$current$ch.length) > 0) {
          var _portalRef$current;
          const firstChild = portalRef === null || portalRef === void 0 || (_portalRef$current = portalRef.current) === null || _portalRef$current === void 0 || (_portalRef$current = _portalRef$current.children) === null || _portalRef$current === void 0 || (_portalRef$current = _portalRef$current[0]) === null || _portalRef$current === void 0 ? void 0 : _portalRef$current.getBoundingClientRect();
          if (firstChild && firstChild.height === 0 && firstChild.width === 0) {
            throwStormError('portal', 'The portal element is currently invisible, either due to its own styles or its parent\'s styles. Ensure the element and its parents are visible in the DOM.');
          }
        }
        // if a duration is set, wait for the popover to mount
      }, transitionDuration !== null && transitionDuration !== void 0 ? transitionDuration : 0);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [portalRef, throwStormError, transitionDuration]);

  // Skip using Portal when disabled or `portalRef` is not defined
  if (disabled || !portalRef) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }

  // When portalRef.current becomes available is it as the portal
  if (portalMounted && portalRef.current) {
    return /*#__PURE__*/createPortal(children, portalRef.current);
  }

  // Do not render any children when the `portalRef.current` is null.
  return null;
};

export { Portal as P };
//# sourceMappingURL=Portal-CfuGnhf3.js.map
